import { useMediaQuery, useTheme } from "@mui/material";
import {
  createContext,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { ampli } from "@/config";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import type { FleetMessageRecipientMode } from "@/types/fleetMessage";
import type { SetStateType } from "@/utils";

interface ToolBarProps {
  offsetWidth: number;
  offsetLeft: number;
}

export type UxState = {
  bothNavOpen?: boolean;
  editWorkspaceSettings?: boolean;
  inviteModalOpen?: boolean;
  navTitle?: string;
  isMdUp?: boolean;
  isSmUp?: boolean;
  leftNavOpen?: boolean;
  rightNavOpen?: boolean;
  recurringScheduleModalOpen?: boolean;
  scheduleMessageModalOpen?: boolean;
  setBothNavOpen: SetStateType<boolean>;
  setEditWorkspaceSettings: SetStateType<boolean>;
  setNavTitle: SetStateType<string>;
  setInviteModalOpen: SetStateType<boolean>;
  setLeftNavOpen: SetStateType<boolean>;
  setRightNavOpen: SetStateType<boolean>;
  setRecurringScheduleModalOpen: SetStateType<boolean>;
  setScheduleMessageModalOpen: SetStateType<boolean>;
  setUserFeedbackModalOpen: SetStateType<boolean>;
  setWorkspaceModalOpen: SetStateType<boolean>;
  toggleInviteModal: () => void;
  toggleLeftNav: () => void;
  toggleRightNav: () => void;
  toggleChannelUsersModal: () => void;
  toggleWorkspaceModal: () => void;
  userFeedbackModalOpen?: boolean;
  workspaceModalOpen?: boolean;
  setGlobalSearchOpen?: SetStateType<boolean>;
  globalSearchOpen?: boolean;
  toolBarSize?: ToolBarProps;
  setToolbarSize?: SetStateType<ToolBarProps>;
  userReadOnlyMode?: boolean;
  setUserReadOnlyMode?: SetStateType<boolean>;
  fleetMessageModalOpen?: boolean;
  setFleetMessageModalOpen?: SetStateType<boolean>;
  setFleetMessageActiveSection?: SetStateType<string | null>;
  fleetMessageActiveSection?: string | null;
  fleetMessageText?: string;
  setFleetMessageText?: SetStateType<string>;
  fleetMessageActiveChannels?: string[];
  setFleetMessageActiveChannels?: SetStateType<string[]>;
  fleetMessageLocation?: string;
  setFleetMessageTableSelection?: SetStateType<string[]>;
  fleetMessageTableSelection?: string[];
  setFleetMessageLocation?: SetStateType<string>;
  fleetMessageRadius?: string;
  setFleetMessageRadius?: SetStateType<string>;
  fleetMessageMeasurementType?: string;
  setFleetMessageMeasurementType?: SetStateType<string>;
  fleetMessageLatitude?: string;
  setFleetMessageLatitude?: SetStateType<string>;
  fleetMessageLongitude?: string;
  setFleetMessageLongitude?: SetStateType<string>;
  fleetMessageFromItem?: boolean;
  setFleetMessageFromItem?: SetStateType<boolean>;
  fleetMessageContentId?: string | null;
  setFleetMessageContentId?: SetStateType<string | null>;
  /** Send Silently; lives here so it survives `returnToDriverTable` the way the attachment id does (WEB-2284). */
  fleetMessageSendSilently?: boolean;
  setFleetMessageSendSilently?: SetStateType<boolean>;
  fleetMessageRecipientMode?: FleetMessageRecipientMode | null;
  setFleetMessageRecipientMode?: SetStateType<FleetMessageRecipientMode | null>;
  returnToDriverTable?: () => void;
  resetFleetValues?: () => void;
  hideDriverMessages?: boolean;
  setHideDriverMessages?: SetStateType<boolean>;
  flushWebSettings?: () => Promise<void>;
  fleetMessageInvalidLocation?: boolean;
  setFleetMessageInvalidLocation?: SetStateType<boolean>;
  selectedChannelItem?: string | null;
  setSelectedChannelItem?: SetStateType<string | null>;
  forceLogout?: boolean;
  setForceLogout?: SetStateType<boolean>;
  rightPanelOpen?: boolean;
  setRightPanelOpen?: SetStateType<boolean>;
  feedViewCollapsed?: boolean;
  setFeedViewCollapsed?: SetStateType<boolean>;
};

//create a context, with createContext api
export const UxContext = createContext<UxState>({
  setBothNavOpen: () => {},
  setEditWorkspaceSettings: () => {},
  setNavTitle: () => {},
  setInviteModalOpen: () => {},
  setLeftNavOpen: () => {},
  setRightNavOpen: () => {},
  setFleetMessageModalOpen: () => {},
  setFleetMessageText: () => {},
  setFleetMessageActiveChannels: () => {},
  setRecurringScheduleModalOpen: () => {},
  setScheduleMessageModalOpen: () => {},
  setUserFeedbackModalOpen: () => {},
  setWorkspaceModalOpen: () => {},
  toggleInviteModal: () => {},
  toggleLeftNav: () => {},
  toggleRightNav: () => {},
  toggleChannelUsersModal: () => {},
  toggleWorkspaceModal: () => {},
  setGlobalSearchOpen: () => {},
  setToolbarSize: () => {},
  setForceLogout: () => {},
  setRightPanelOpen: () => {},
  setFleetMessageTableSelection: () => {},
});

type Props = {
  children: ReactNode | ReactNode[];
};

const UxStateProvider = ({ children }: Props) => {
  const [rightPanelOpen, setRightPanelOpen] = useState<boolean>(false);
  const [leftNavOpen, setLeftNavOpen] = useState<boolean>(true);
  const [rightNavOpen, setRightNavOpen] = useState<boolean>(false);
  const [bothNavOpen, setBothNavOpen] = useState(leftNavOpen && rightNavOpen);
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const [channelUsersModalOpen, setChannelUsersModalOpen] = useState<boolean>(false);
  const [fleetMessageModalOpen, setFleetMessageModalOpen] = useState<boolean>(false);
  const [fleetMessageText, setFleetMessageText] = useState<string>("");
  const [fleetMessageActiveChannels, setFleetMessageActiveChannels] = useState<string[]>([]);
  const [scheduleMessageModalOpen, setScheduleMessageModalOpen] = useState<boolean>(false);
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState<boolean>(false);
  const [editChannelSettings, setEditChannelSettings] = useState<boolean>(false);
  const [editWorkspaceSettings, setEditWorkspaceSettings] = useState<boolean>(false);
  const [navTitle, setNavTitle] = useState<string>("");
  const { settings, updateWebSettings, flushWebSettings } = useWebSettings();
  const userReadOnlyMode = settings.userReadOnlyMode;
  const setUserReadOnlyMode = useCallback(
    (value: SetStateAction<boolean>) => {
      const next = typeof value === "function" ? value(settings.userReadOnlyMode) : value;
      updateWebSettings({ userReadOnlyMode: next });
    },
    [settings.userReadOnlyMode, updateWebSettings],
  );
  const [recurringScheduleModalOpen, setRecurringScheduleModalOpen] = useState<boolean>(false);
  const [userFeedbackModalOpen, setUserFeedbackModalOpen] = useState<boolean>(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState<boolean>(false);
  const [toolBarSize, setToolbarSize] = useState<ToolBarProps>({} as ToolBarProps);
  const hideDriverMessages = settings.hideDriverMessages;
  const setHideDriverMessages = useCallback(
    (value: SetStateAction<boolean>) => {
      const next = typeof value === "function" ? value(settings.hideDriverMessages) : value;
      updateWebSettings({ hideDriverMessages: next });
    },
    [settings.hideDriverMessages, updateWebSettings],
  );

  const [fleetMessageLocation, setFleetMessageLocation] = useState<string>("");
  const [fleetMessageRadius, setFleetMessageRadius] = useState<string>("100");
  const [fleetMessageMeasurementType, setFleetMessageMeasurementType] = useState<string>("miles");
  const [fleetMessageLatitude, setFleetMessageLatitude] = useState<string>("");
  const [fleetMessageLongitude, setFleetMessageLongitude] = useState<string>("");
  const [selectedChannelItem, setSelectedChannelItem] = useState<string>(null);
  const [fleetMessageFromItem, setFleetMessageFromItem] = useState<boolean>(false);
  const [forceLogout, setForceLogout] = useState<boolean>(false);
  const [fleetMessageInvalidLocation, setFleetMessageInvalidLocation] = useState<boolean>(false);
  const [fleetMessageActiveSection, setFleetMessageActiveSection] = useState<string | null>(null);
  const [feedViewCollapsed, setFeedViewCollapsed] = useState<boolean>(false);
  const [fleetMessageTableSelection, setFleetMessageTableSelection] = useState<string[]>([]);
  const [fleetMessageContentId, setFleetMessageContentId] = useState<string | null>(null);
  const [fleetMessageSendSilently, setFleetMessageSendSilently] = useState<boolean>(false);
  const [fleetMessageRecipientMode, setFleetMessageRecipientMode] = useState<FleetMessageRecipientMode | null>(null);

  /**
   * Closes the fleet-message pane over the driver table without discarding the draft.
   *
   * The message body, its attachment and the chosen recipient option stay in context so reopening the pane from the
   * table's Fleet Message action resumes the same draft (WEB-2272); only the pane's visibility changes here.
   */
  const returnToDriverTable = useCallback(() => {
    setFleetMessageModalOpen(() => false);
  }, []);

  const resetFleetValues = () => {
    setFleetMessageLongitude(() => "");
    setFleetMessageLatitude(() => "");
    setFleetMessageRadius(() => "100");
    setFleetMessageLocation(() => "");
    setFleetMessageText(() => "");
    setFleetMessageModalOpen(() => false);
    setFleetMessageFromItem(() => false);
    setFleetMessageInvalidLocation(() => false);
    setFleetMessageActiveChannels(() => []);
    setFleetMessageActiveSection(() => null);
    setFleetMessageTableSelection(() => []);
    setFleetMessageContentId(() => null);
    setFleetMessageSendSilently(() => false);
    setFleetMessageRecipientMode(() => null);
  };

  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const toggleLeftNav = () => {
    const leftNavValue = !leftNavOpen;
    setLeftNavOpen(() => leftNavValue);
    setRightNavOpen(() => false);
    ampli.feedsListOpen();
  };

  const toggleRightNav = () => {
    const rightNavValue = !rightNavOpen;
    if (rightNavValue) {
      setLeftNavOpen(() => false);
    } else {
      setLeftNavOpen(() => true);
    }
    setRightNavOpen(() => rightNavValue);
  };

  useEffect(() => {
    // on screen size changes from large to medium, lets close both navs and only allow one open at a time
    if (!isMdUp) {
      setLeftNavOpen(false);
      setRightNavOpen(false);
    } else {
      setLeftNavOpen(true);
      setRightNavOpen(false);
    }
  }, [isMdUp]);

  const toggleInviteModal = () => {
    setInviteModalOpen(!inviteModalOpen);
  };

  const toggleChannelUsersModal = () => {
    setChannelUsersModalOpen(!channelUsersModalOpen);
  };

  const toggleWorkspaceModal = () => {
    setWorkspaceModalOpen(!workspaceModalOpen);
  };

  const uxState: UxState = {
    bothNavOpen,
    editWorkspaceSettings,
    fleetMessageModalOpen,
    inviteModalOpen,
    navTitle,
    isMdUp,
    isSmUp,
    leftNavOpen,
    rightNavOpen,
    recurringScheduleModalOpen,
    scheduleMessageModalOpen,
    setBothNavOpen,
    setEditWorkspaceSettings,
    setFleetMessageModalOpen,
    setInviteModalOpen,
    setLeftNavOpen,
    setRightNavOpen,
    setNavTitle,
    setRecurringScheduleModalOpen,
    setScheduleMessageModalOpen,
    setUserFeedbackModalOpen,
    setWorkspaceModalOpen,
    toggleInviteModal,
    toggleLeftNav,
    toggleRightNav,
    toggleChannelUsersModal,
    toggleWorkspaceModal,
    userFeedbackModalOpen,
    workspaceModalOpen,
    globalSearchOpen,
    setGlobalSearchOpen,
    toolBarSize,
    setUserReadOnlyMode,
    userReadOnlyMode,
    setToolbarSize,
    fleetMessageText,
    setFleetMessageText,
    fleetMessageActiveChannels,
    setFleetMessageActiveChannels,
    fleetMessageLocation,
    setFleetMessageLocation,
    fleetMessageRadius,
    setFleetMessageRadius,
    fleetMessageMeasurementType,
    setFleetMessageMeasurementType,
    fleetMessageLatitude,
    setFleetMessageLatitude,
    fleetMessageLongitude,
    setFleetMessageLongitude,
    resetFleetValues,
    fleetMessageFromItem,
    setFleetMessageFromItem,
    hideDriverMessages,
    setHideDriverMessages,
    flushWebSettings,
    fleetMessageInvalidLocation,
    setFleetMessageInvalidLocation,
    selectedChannelItem,
    setSelectedChannelItem,
    forceLogout,
    setForceLogout,
    fleetMessageActiveSection,
    setFleetMessageActiveSection,
    rightPanelOpen,
    setRightPanelOpen,
    feedViewCollapsed,
    setFeedViewCollapsed,
    fleetMessageTableSelection,
    setFleetMessageTableSelection,
    fleetMessageContentId,
    setFleetMessageContentId,
    fleetMessageSendSilently,
    setFleetMessageSendSilently,
    fleetMessageRecipientMode,
    setFleetMessageRecipientMode,
    returnToDriverTable,
  };

  return <UxContext.Provider value={uxState}>{children}</UxContext.Provider>;
};

export default UxStateProvider;
